const DEFAULT_RELOAD_KEY = 'superclinic:chunk-load-reload-attempted';

// Minimum gap between automatic reloads. A persistently missing chunk can
// trigger at most one reload per window before the error surfaces to the
// nearest error boundary, while a genuine new incident (next deploy) can
// still self-heal once the window has passed.
export const RELOAD_SUPPRESSION_WINDOW_MS = 60_000;

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load module script/i,
  /Expected a JavaScript-or-Wasm module script/i,
  /MIME type of ["']?text\/html/i,
  /Loading chunk \d+ failed/i,
  /ChunkLoadError/i,
];

const getSessionStorageItem = (key: string): string | null => {
  try {
    return window.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const setSessionStorageItem = (key: string, value: string): boolean => {
  try {
    window.sessionStorage?.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

export function isDynamicImportError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : '';

  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Reload the page to recover from a stale-deploy chunk failure, at most once
 * per suppression window. The marker stores the reload timestamp and is never
 * cleared on success: many lazy imports share the default key, so a sibling
 * chunk loading fine (e.g. a lazy layout above a missing lazy page) must not
 * re-arm the guard — that would reload forever on a persistently missing
 * chunk. Returns true when a reload was initiated.
 */
export function reloadOnceForChunkError(reloadKey = DEFAULT_RELOAD_KEY): boolean {
  const lastReloadAt = Number(getSessionStorageItem(reloadKey));
  if (Number.isFinite(lastReloadAt) && Date.now() - lastReloadAt < RELOAD_SUPPRESSION_WINDOW_MS) {
    return false;
  }

  // Only reload when the marker can be persisted; otherwise a blocked
  // storage environment would reload forever on a persistent chunk error.
  if (!setSessionStorageItem(reloadKey, String(Date.now()))) {
    return false;
  }

  window.location.reload();
  return true;
}

export async function importWithChunkReload<T>(
  loader: () => Promise<T>,
  reloadKey = DEFAULT_RELOAD_KEY
): Promise<T> {
  try {
    return await loader();
  } catch (error) {
    if (!isDynamicImportError(error)) {
      throw error;
    }

    reloadOnceForChunkError(reloadKey);

    throw error;
  }
}
